import type { CmdbAttribute, CmdbCardRow } from '../../lib/types';

export interface ComponentGroup {
  key: string;
  component: CmdbCardRow | null;
  versions: CmdbCardRow[];
  name: string;
  manufacturer: string;
}

export type ComponentGroupFilter = 'all' | 'with-versions' | 'without-versions';

const naturalOrder = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

function textValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function field(row: CmdbCardRow, name: string): string {
  return textValue(row.display[name]) || textValue(row.values[name]);
}

function componentReference(row: CmdbCardRow): number | null {
  const raw = row.values.KomponenteTypeID;
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function versionValue(row: CmdbCardRow): string {
  return field(row, 'Version') || field(row, 'FirmwareVersion');
}

export function componentVersionLabel(row: CmdbCardRow): string {
  return versionValue(row) || 'Nicht angegeben';
}

/**
 * Natural descending order ("3.10" before "3.9"). The first entry with a version
 * counts as the latest release; pre-release suffixes are not interpreted.
 */
function compareVersions(left: CmdbCardRow, right: CmdbCardRow): number {
  const a = versionValue(left);
  const b = versionValue(right);
  if (!a || !b) return a ? -1 : b ? 1 : left.id - right.id;
  return naturalOrder.compare(b, a) || left.id - right.id;
}

/** Fields that identify one release; a new version must enter its own. */
const RELEASE_FIELDS = new Set(['Version', 'Update']);

/** The highest version of a group from `groupComponents`, or `null` if none has a version. */
export function latestComponentVersion(group: ComponentGroup): CmdbCardRow | null {
  return group.versions.find((version) => versionValue(version) !== '') ?? null;
}

/**
 * Form defaults for a new version: the latest version's attributes without its
 * release identity, pipeline fields or links, because a new release has to be
 * assessed on its own. Name and manufacturer follow the component's master data.
 */
export function newVersionValues(
  attributes: readonly CmdbAttribute[],
  component: CmdbCardRow,
  latest: CmdbCardRow | null,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const attr of latest ? attributes : []) {
    if (attr.system || attr.type === 'reference' || RELEASE_FIELDS.has(attr.name)) continue;
    const value = latest!.values[attr.name];
    if (value !== null && value !== undefined && value !== '') values[attr.name] = value;
  }
  values.KomponenteTypeID = component.id;
  values.Bezeichnung = field(component, 'Bezeichnung') || values.Bezeichnung || component.label;
  values.Hersteller = field(component, 'Hersteller') || values.Hersteller || '';
  return values;
}

/** Keep every version, including records whose component is missing or not assigned. */
export function groupComponents(
  components: readonly CmdbCardRow[],
  versions: readonly CmdbCardRow[],
): ComponentGroup[] {
  const groups = new Map<string, ComponentGroup>();
  for (const component of components) {
    const key = `component:${component.id}`;
    groups.set(key, {
      key,
      component,
      versions: [],
      name: field(component, 'Bezeichnung') || component.label || 'Unbenannte Komponente',
      manufacturer: field(component, 'Hersteller'),
    });
  }

  for (const version of versions) {
    const reference = componentReference(version);
    const name = field(version, 'Bezeichnung');
    const manufacturer = field(version, 'Hersteller');
    // A real reference is authoritative, even when its target is absent. Never
    // attach a dangling version to an unrelated component with the same label.
    const key = reference !== null
      ? `component:${reference}`
      : name
        ? `unassigned:${JSON.stringify([name, manufacturer])}`
        : `unassigned-version:${version.id}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        component: null,
        versions: [],
        name: name || field(version, 'KomponenteTypeID') || version.label || 'Nicht zugeordnete Komponente',
        manufacturer,
      };
      groups.set(key, group);
    }
    group.versions.push(version);
    if (!group.manufacturer) group.manufacturer = manufacturer;
  }

  return [...groups.values()]
    .map((group) => ({ ...group, versions: [...group.versions].sort(compareVersions) }))
    .sort((a, b) => naturalOrder.compare(a.name, b.name)
      || naturalOrder.compare(a.manufacturer, b.manufacturer)
      || naturalOrder.compare(a.key, b.key));
}

function rowSearchText(row: CmdbCardRow): string {
  return [row.label, row.code, row.id, ...Object.values(row.display), ...Object.values(row.values)]
    .map(textValue)
    .join(' ');
}

/** Matching one version keeps its full component group visible for context. */
export function filterComponentGroups(
  groups: readonly ComponentGroup[],
  search: string,
  filter: ComponentGroupFilter,
): ComponentGroup[] {
  const terms = search.trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
  return groups.filter((group) => {
    if (filter === 'with-versions' && group.versions.length === 0) return false;
    if (filter === 'without-versions' && group.versions.length !== 0) return false;
    if (terms.length === 0) return true;
    const searchable = [
      group.name,
      group.manufacturer,
      group.component ? rowSearchText(group.component) : '',
      ...group.versions.map(rowSearchText),
    ].join(' ').toLocaleLowerCase('de');
    return terms.every((term) => searchable.includes(term));
  });
}

export function componentKindLabel(group: ComponentGroup): string {
  const labels: Record<string, string> = {
    a: 'Software',
    o: 'Betriebssystem',
    h: 'Hardware',
  };
  const kinds = new Set<string>();
  for (const row of group.component ? [group.component, ...group.versions] : group.versions) {
    const kind = labels[field(row, 'Part').toLowerCase()];
    if (kind) kinds.add(kind);
  }
  if (kinds.size > 1) return 'Gemischt';
  return [...kinds][0] || 'Nicht angegeben';
}
